import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BellRinging, ArrowRight } from "@phosphor-icons/react";

/**
 * Compact list of the most-recent watchlist alerts on the Dashboard so users
 * spot big movers without leaving home. Hides itself entirely when nothing
 * has fired yet — keeps a fresh dashboard clean.
 */
export default function AlertFeedCard({ limit = 5 }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let stop = false;
    const load = () =>
      api.get(`/notifications?kind=watchlist_alert&limit=${limit}`)
        .then((r) => { if (!stop) setItems(r.data.notifications || []); })
        .catch(() => { if (!stop) setItems([]); })
        .finally(() => { if (!stop) setLoaded(true); });
    load();
    const t = setInterval(load, 60_000);
    return () => { stop = true; clearInterval(t); };
  }, [limit]);

  if (!loaded) return null;
  if (items.length === 0) return null;

  return (
    <div
      className="p-4 border border-zinc-800 rounded-sm bg-zinc-900/40"
      data-testid="alert-feed-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
          <BellRinging size={11} weight="fill" className="text-[#00C805]" />
          Recent movers
        </div>
        <Link
          to="/notifications"
          className="mono text-[9px] text-zinc-500 hover:text-white uppercase tracking-widest inline-flex items-center gap-1"
          data-testid="alert-feed-see-all"
        >
          See all <ArrowRight size={9} weight="bold" />
        </Link>
      </div>
      <ul className="space-y-2">
        {items.map((n) => {
          const chg = n.meta?.change_pct;
          const sym = n.meta?.symbol;
          const up = (chg ?? 0) >= 0;
          return (
            <li
              key={n.id}
              data-testid={`alert-feed-item-${sym || n.id}`}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <Link
                to={`/trade?s=${sym || ""}`}
                className="mono text-white hover:text-[#00C805] font-semibold"
              >
                {sym || "—"}
              </Link>
              <div className="flex-1 mono text-[10px] text-zinc-500 truncate">
                {n.at ? new Date(n.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
              </div>
              {chg != null && (
                <span
                  className={`mono text-xs font-bold ${up ? "profit" : "loss"}`}
                  data-testid={`alert-feed-change-${sym || n.id}`}
                >
                  {up ? "↑" : "↓"} {Math.abs(chg).toFixed(2)}%
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
